import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('idle'); // idle, processing, success, error
  const [message, setMessage] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const adminVerified = useRef(false);

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user || session.user.email !== 'admin@tankcare.com') {
        navigate('/');
        return;
      }
      setUser(session.user);
      adminVerified.current = true;
      setLoading(false);
    };
    if (!adminVerified.current) {
      checkAdmin();
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setStatus('idle');

    // Validate current password
    if (!currentPassword) {
      setMessage('Please enter your current password.');
      setStatus('error');
      return;
    }

    // Validate new password
    if (!newPassword || newPassword.length < 6) {
      setMessage('New password must be at least 6 characters.');
      setStatus('error');
      return;
    }

    // Validate confirm password
    if (newPassword !== confirmPassword) {
      setMessage('New passwords do not match.');
      setStatus('error');
      return;
    }

    // Prevent same password
    if (currentPassword === newPassword) {
      setMessage('New password must be different from current password.');
      setStatus('error');
      return;
    }

    setStatus('processing');

    try {
      // Step 1: Verify current password by re-authenticating
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) {
        setMessage('Current password is incorrect.');
        setStatus('error');
        return;
      }

      // Step 2: Update password via Supabase
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setMessage(updateError.message || 'Failed to update password. Please try again.');
        setStatus('error');
        return;
      }

      setMessage('Password updated successfully!');
      setStatus('success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Reset success message after 3 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 3000);
    } catch (err) {
      setMessage('An unexpected error occurred. Please try again.');
      setStatus('error');
    }
  };

  if (loading) {
    return (
      <main className="pt-40 flex-grow w-full max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop pb-12 md:pb-16 flex items-center justify-center">
        <p className="text-on-surface-variant font-body-md">Loading...</p>
      </main>
    );
  }

  return (
    <>
      <main className="pt-40 flex-grow w-full max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop pb-12 md:pb-16">
        <div className="max-w-xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-1 text-on-surface-variant hover:text-primary transition-colors mb-6 group"
            >
              <span className="material-symbols-outlined text-[20px] group-hover:-translate-x-1 transition-transform">arrow_back</span>
              <span className="font-body-md">Back to Dashboard</span>
            </button>
            <h1 className="font-display-lg text-display-lg-mobile md:text-3xl text-on-surface mb-2">Reset Password</h1>
            <p className="font-body-md text-on-surface-variant">Update your admin account password securely.</p>
          </div>

          {/* Password Form Card */}
          <div className="bg-surface-container-lowest border border-surface-variant rounded-2xl p-6 md:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
            
            {/* Admin Badge */}
            <div className="flex items-center gap-3 mb-8 pb-6 border-b border-surface-variant">
              <div className="w-10 h-10 rounded-full bg-primary-container/10 flex items-center justify-center">
                <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>admin_panel_settings</span>
              </div>
              <div>
                <p className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">Signed in as</p>
                <p className="font-body-md text-on-surface font-medium">{user?.email}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Current Password */}
              <div>
                <label className="block font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant text-[20px]">lock</span>
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="w-full pl-12 pr-12 py-3.5 border border-outline-variant rounded-xl bg-surface-bright focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 font-body-md text-on-surface transition-all"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface transition-colors"
                  >
                    <span className="material-symbols-outlined text-[20px]">{showCurrent ? 'visibility_off' : 'visibility'}</span>
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-surface-variant"></div>
                <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">New Password</span>
                <div className="flex-1 h-px bg-surface-variant"></div>
              </div>

              {/* New Password */}
              <div>
                <label className="block font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider mb-2">
                  New Password
                </label>
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant text-[20px]">key</span>
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full pl-12 pr-12 py-3.5 border border-outline-variant rounded-xl bg-surface-bright focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 font-body-md text-on-surface transition-all"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface transition-colors"
                  >
                    <span className="material-symbols-outlined text-[20px]">{showNew ? 'visibility_off' : 'visibility'}</span>
                  </button>
                </div>
                {newPassword && newPassword.length < 6 && (
                  <p className="mt-2 text-xs text-error flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">warning</span>
                    Must be at least 6 characters
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant text-[20px]">key</span>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full pl-12 pr-12 py-3.5 border border-outline-variant rounded-xl bg-surface-bright focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 font-body-md text-on-surface transition-all"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface transition-colors"
                  >
                    <span className="material-symbols-outlined text-[20px]">{showConfirm ? 'visibility_off' : 'visibility'}</span>
                  </button>
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="mt-2 text-xs text-error flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">warning</span>
                    Passwords do not match
                  </p>
                )}
                {confirmPassword && newPassword === confirmPassword && confirmPassword.length >= 6 && (
                  <p className="mt-2 text-xs text-primary flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">check_circle</span>
                    Passwords match
                  </p>
                )}
              </div>

              {/* Status Message */}
              {message && (
                <div className={`flex items-center gap-3 p-4 rounded-xl border ${
                  status === 'success' 
                    ? 'bg-[#E6F4EA] border-[#1E8E3E]/20 text-[#1E8E3E]' 
                    : 'bg-[#FCE8E6] border-[#D93025]/20 text-[#D93025]'
                }`}>
                  <span className="material-symbols-outlined text-[20px]">
                    {status === 'success' ? 'check_circle' : 'error'}
                  </span>
                  <span className="font-body-md">{message}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={status === 'processing' || !currentPassword || !newPassword || !confirmPassword}
                className="w-full bg-primary text-on-primary py-4 rounded-xl font-label-lg text-label-lg tracking-wide hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {status === 'processing' ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : status === 'success' ? (
                  <>
                    <span className="material-symbols-outlined text-[20px]">check_circle</span>
                    Password Updated!
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-[20px]">lock_reset</span>
                    Update Password
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ResetPassword;
